/**
 * Application routing configuration
 * @author Maheen Nasir
 * @description Defines the application routes with lazy loading and suspense handling
 */

import { lazy, Suspense } from "react";
import { Navigate, type RouteObject } from "react-router-dom";
import type { ReactElement, ComponentType } from "react";
import Spinner from "@components/common/Spinner";
import { BROWSER_ROUTES } from "@constants/browser.routes";
import AuthGuard from "@components/RouteGuards/AuthGuard";
import GuestGuard from "@components/RouteGuards/GuestGuard";
import ResetRouteGaurd from "@components/RouteGuards/ResetGuard";
import OtpVerificationGuard from "@components/RouteGuards/OtpVerificationGuard";
import FacultyAccessGuard from "@components/RouteGuards/FacultyAccessGuard";
import { MainLayout } from "@components/Layout/MainLayout";

// Lazy load components
const NotFound = lazy(() => import("@pages/404"));
const Unauthorized = lazy(() => import("@pages/Unauthorized"));
const InternalServerError = lazy(() => import("@pages/500"));
const Login = lazy(() => import("@pages/Login"));
const Signup = lazy(() => import("@pages/Signup"));
const ForgetPassword = lazy(() => import("@pages/ForgetPassword"));
const VerifyOtp = lazy(() => import("@pages/VerifyOtp"));
const ResetPassword = lazy(() => import("@pages/ResetPassword"));
const SetPassword = lazy(() => import("@pages/SetPassword"));
const PrivacyPolicy = lazy(() => import("@pages/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("@pages/TermsAndConditions"));
const UnregisteredInstitutes = lazy(() => import("@pages/UnregisteredInstitutes"));
const ReportUnregisteredInstitute = lazy(() => import("@pages/ReportUnregisteredInstitute"));
const CertificateVerification = lazy(() => import("@pages/CertificateVerification"));
const ViewCertificate = lazy(() => import("@pages/ViewCertificate"));
const PublicCourseCatalog = lazy(() => import("@pages/PublicCourseCatalog"));
const ApplicationsPage = lazy(() => import("@features/Applications/Applications"));
const AdmissionsPage = lazy(() => import("@features/Admissions/Admissions"));
const ReviewApplicationsPage = lazy(() => import("@features/ApplicationsReview/ReviewApplication"));
const TrackApplicationPage = lazy(() => import("@features/TrackApplication/TrackApplication"));
const EChallanStatusPage = lazy(() => import("@features/EChallanStatus/EChallanStatus"));
const EChallanSummaryPage = lazy(() => import("@features/EChallanSummary/EChallanSummary"));
const Dashboard = lazy(() => import("@components/Dashboard/Dashboard"));
const InstituteStatsDashboard = lazy(() => import("@features/InstituteStatsDashboard/InstituteStatsDashboard"));
const AcademicCalendarPage = lazy(() => import("@pages/AcademicCalendar"));
const AcademicCalendarCoursesPage = lazy(() => import("@pages/AcademicCalendarCourses"));
const RegisterInstitute = lazy(() => import("@pages/RegisterInstitute"));
const AddCourses = lazy(() => import("@pages/AddCourses"));
const ReferbackPage = lazy(() => import("@pages/Referback"));
const Courses = lazy(() => import("@pages/Courses"));
const OpenAdmission = lazy(() => import("@pages/OpenAdmission"));
const ManageSessions = lazy(() => import("@pages/ManageSessions"));
const CourseDetails = lazy(() => import("@pages/CourseDetails"));
const CourseDetailsPage = lazy(() => import("@features/Courses/pages/CourseDetailsPage"));
const Faculty = lazy(() => import("@pages/Faculty"));
const RenewalCoursesPage = lazy(() => import("@pages/RenewalCourses"));
const Attendance = lazy(() => import("@pages/Attendance"));
const FacultyAttendance = lazy(() => import("@pages/FacultyAttendance"));
const Promotion = lazy(() => import("@pages/Promotion"));
const Dropout = lazy(() => import("@pages/Dropout"));
const MigrationRequests = lazy(() => import("@pages/MigrationRequests"));
const EmploymentStatus = lazy(() => import("@pages/EmploymentStatus"));
const ManageOjt = lazy(() => import("@pages/ManageOjt"));
const ManageOjtSession = lazy(() => import("@pages/ManageOjtSession"));
const ResultManagementPage = lazy(() => import("@pages/ResultManagement"));
const SessionStudentsPage = lazy(() => import("@pages/SessionStudents"));
const StudentCertificatesPage = lazy(() => import("@pages/StudentCertificates"));

/**
 * Higher-order component to wrap lazy-loaded components with Suspense
 * @param {React.LazyExoticComponent<ComponentType>} Component - The lazy-loaded component to wrap
 * @returns {JSX.Element} The wrapped component with Suspense
 */
const withSuspense = (Component: React.LazyExoticComponent<ComponentType>): ReactElement => {
	return (
		<Suspense fallback={<Spinner overlay />}>
			<Component />
		</Suspense>
	);
};

/**
 * Application route configuration
 * @description Defines the main routes of the application with their corresponding components
 */
export const routes: RouteObject[] = [
	{
		path: "/",
		element: (
			<AuthGuard>
				<OtpVerificationGuard>
					<FacultyAccessGuard>
						<MainLayout />
					</FacultyAccessGuard>
				</OtpVerificationGuard>
			</AuthGuard>
		),
		children: [
			{ index: true, element: withSuspense(Dashboard) },
			{ path: "institute-stats-dashboard", element: withSuspense(InstituteStatsDashboard) },
			{ path: "academic-calendar", element: withSuspense(AcademicCalendarPage) },
			{ path: "academic-calendar/:applicationSessionUuid/courses", element: withSuspense(AcademicCalendarCoursesPage) },
			{ path: "applications", element: withSuspense(ApplicationsPage) },
			{ path: "admissions", element: withSuspense(AdmissionsPage) },
			{ path: "review-applications", element: withSuspense(ReviewApplicationsPage) },
			{ path: "review-applications/*", element: withSuspense(ReviewApplicationsPage) },
			{ path: "track-application", element: withSuspense(TrackApplicationPage) },
			{ path: "e-challan-status", element: withSuspense(EChallanStatusPage) },
			{ path: "e-challan-summary", element: withSuspense(EChallanSummaryPage) },
			{ path: "register-institute", element: withSuspense(RegisterInstitute) },
			{ path: "register-institute/*", element: withSuspense(RegisterInstitute) },
			{ path: BROWSER_ROUTES.COURSE_DETAILS, element: withSuspense(CourseDetails) },
			{ path: BROWSER_ROUTES.COURSE_DETAILS + "/*", element: withSuspense(CourseDetails) },
			{ path: "course-details-page/:courseId", element: withSuspense(CourseDetailsPage) },
			{ path: BROWSER_ROUTES.RENEWAL_COURSES, element: withSuspense(RenewalCoursesPage) },
			{ path: BROWSER_ROUTES.ADD_COURSES, element: withSuspense(AddCourses) },
			{ path: BROWSER_ROUTES.COURSES, element: withSuspense(Courses) },
			{ path: BROWSER_ROUTES.OPEN_ADMISSION, element: withSuspense(OpenAdmission) },
			{ path: BROWSER_ROUTES.MANAGE_SESSIONS, element: withSuspense(ManageSessions) },
			{ path: BROWSER_ROUTES.FACULTY, element: withSuspense(Faculty) },
			{ path: BROWSER_ROUTES.REFERBACK + "/:uuid", element: withSuspense(ReferbackPage) },
			{ path: BROWSER_ROUTES.REFERBACK + "/:uuid/:step", element: withSuspense(ReferbackPage) },
			{ path: BROWSER_ROUTES.ATTENDANCE, element: withSuspense(Attendance) },
			{ path: BROWSER_ROUTES.STUDENT_ATTENDANCE, element: withSuspense(Attendance) },
			{ path: BROWSER_ROUTES.FACULTY_ATTENDANCE, element: withSuspense(FacultyAttendance) },
			{ path: BROWSER_ROUTES.PROMOTION, element: withSuspense(Promotion) },
			{ path: BROWSER_ROUTES.DROPOUT, element: withSuspense(Dropout) },
			{ path: BROWSER_ROUTES.MIGRATION_REQUESTS, element: withSuspense(MigrationRequests) },
			{ path: BROWSER_ROUTES.EMPLOYMENT_STATUS, element: withSuspense(EmploymentStatus) },
			{ path: BROWSER_ROUTES.MANAGE_OJT, element: withSuspense(ManageOjt) },
			{ path: BROWSER_ROUTES.MANAGE_OJT_SESSION, element: withSuspense(ManageOjtSession) },
			{ path: BROWSER_ROUTES.RESULT_MANAGEMENT, element: withSuspense(ResultManagementPage) },
			{ path: BROWSER_ROUTES.SESSION_STUDENTS, element: withSuspense(SessionStudentsPage) },
			{ path: BROWSER_ROUTES.STUDENT_CERTIFICATES, element: withSuspense(StudentCertificatesPage) },
		],
	},
	{
		path: BROWSER_ROUTES.LOGIN,
		element: <GuestGuard>{withSuspense(Login)}</GuestGuard>,
	},
	{
		path: BROWSER_ROUTES.SIGNUP,
		element: <GuestGuard>{withSuspense(Signup)}</GuestGuard>,
	},

	{
		path: BROWSER_ROUTES.FORGET_PASSWORD,
		element: <GuestGuard>{withSuspense(ForgetPassword)}</GuestGuard>,
	},
	{
		path: BROWSER_ROUTES.VERIFY_OTP,
		element: <GuestGuard>{withSuspense(VerifyOtp)}</GuestGuard>,
	},
	{
		path: BROWSER_ROUTES.RESET_PASSWORD,
		element: (
			<GuestGuard>
				<ResetRouteGaurd>{withSuspense(ResetPassword)}</ResetRouteGaurd>
			</GuestGuard>
		),
	},
	{
		path: `${BROWSER_ROUTES.SET_PASSWORD}/:token`,
		element: withSuspense(SetPassword),
	},
	{
		path: BROWSER_ROUTES.UNAUTHORIZED,
		element: withSuspense(Unauthorized),
	},
	{
		path: BROWSER_ROUTES.SERVER_ERROR,
		element: withSuspense(InternalServerError),
	},
	{
		path: BROWSER_ROUTES.PRIVACY_POLICY,
		element: withSuspense(PrivacyPolicy),
	},
	{
		path: BROWSER_ROUTES.TERMS_AND_CONDITIONS,
		element: withSuspense(TermsAndConditions),
	},
	{
		path: BROWSER_ROUTES.UNREGISTERED_INSTITUTES,
		element: withSuspense(UnregisteredInstitutes),
	},
	{
		path: BROWSER_ROUTES.REPORT_UNREGISTERED_INSTITUTE,
		element: withSuspense(ReportUnregisteredInstitute),
	},
	{
		path: BROWSER_ROUTES.CERTIFICATE_VERIFICATION,
		element: withSuspense(CertificateVerification),
	},
	{
		path: BROWSER_ROUTES.VIEW_CERTIFICATE,
		element: withSuspense(ViewCertificate),
	},
	{
		path: BROWSER_ROUTES.PUBLIC_COURSE_CATALOG,
		element: <Navigate to={BROWSER_ROUTES.PUBLIC_COURSE_CATALOG_TECHNICAL} replace />,
	},
	{
		path: BROWSER_ROUTES.PUBLIC_COURSE_CATALOG_TECHNICAL,
		element: withSuspense(PublicCourseCatalog),
	},
	{
		path: BROWSER_ROUTES.PUBLIC_COURSE_CATALOG_VOCATIONAL,
		element: withSuspense(PublicCourseCatalog),
	},
	{
		path: "/public/courses",
		element: <Navigate to={BROWSER_ROUTES.PUBLIC_COURSE_CATALOG_TECHNICAL} replace />,
	},
	{
		path: "/public/courses-technical",
		element: <Navigate to={BROWSER_ROUTES.PUBLIC_COURSE_CATALOG_TECHNICAL} replace />,
	},
	{
		path: "/public/courses-vocational",
		element: <Navigate to={BROWSER_ROUTES.PUBLIC_COURSE_CATALOG_VOCATIONAL} replace />,
	},
	{
		path: "*",
		element: withSuspense(NotFound),
	},
];

export default routes;
